/**
 * Legal pack: the retained archive inventory, the current managed analysis (the
 * result authority), uploads and analysis requests. Parsers are ported from the
 * workbench and fail closed: a payload that breaks the contract becomes an
 * ApiError rather than a partially rendered legal conclusion. Document content
 * is evidence for display, never instructions.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from './http'
import { keys } from './queries'

/* ---------- safe URLs (document links come from the API) ---------- */

const APP_ORIGIN = 'https://propalpha.invalid'
const CONTROL = /[\u0000-\u001f\u007f]/

export function safeExternalHttpUrl(value: string | null | undefined): string | null {
  if (!value || value !== value.trim() || CONTROL.test(value)) return null
  try {
    const url = new URL(value)
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null
    return url.toString()
  } catch {
    return null
  }
}

export function safeSameOriginPath(value: string | null | undefined): string | null {
  if (!value?.startsWith('/') || value.startsWith('//') || value.includes('\\') || CONTROL.test(value)) return null
  try {
    const url = new URL(value, APP_ORIGIN)
    if (url.origin !== APP_ORIGIN || url.pathname.startsWith('//')) return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

export function safeDocumentUrl(value: string | null | undefined): string | null {
  return safeSameOriginPath(value) ?? safeExternalHttpUrl(value)
}

/* ---------- contract ---------- */

export const LEGAL_CHECKPOINT_IDS = [
  'archive_integrity', 'document_inventory', 'property_identity', 'seller_capacity', 'title_and_tenure',
  'charges_and_restrictions', 'contract_and_completion', 'buyer_costs', 'occupancy_and_tenancies',
  'searches_and_notices', 'planning_and_use', 'management_and_service_charge', 'insurance_and_risk', 'epc_and_compliance',
] as const
export type LegalCheckpointId = typeof LEGAL_CHECKPOINT_IDS[number]
export type LegalApplicability = 'applies' | 'does_not_apply' | 'uncertain'
export type LegalEvidenceStatus = 'supported' | 'missing' | 'conflicting' | 'not_checked'
export type LegalActionStatus = 'none' | 'routine_action' | 'clarification' | 'specialist_resolution' | 'blocker' | 'resolved'

export interface LegalPackPreparedPage { page: number; imageUrl: string; mimeType: string | null; width: number | null; height: number | null; textChars: number | null; visionRequired: boolean | null }

export interface LegalPackPreparedAssets {
  cacheKey: string
  markdownUrl: string
  markdownSource: 'deterministic' | 'vision'
  deterministicMarkdownUrl: string
  textUrl: string | null
  pageIndexUrl: string | null
  pageCount: number | null
  markdownChars: number | null
  textChars: number | null
  requiresVisualReview: boolean | null
  pages: LegalPackPreparedPage[]
}

export interface LegalPackDocumentSummary {
  id: string
  archiveId: string
  documentName: string
  url: string | null
  preparedAssets: LegalPackPreparedAssets | null
  processing?: { status: 'queued' | 'running' | 'completed' | 'failed'; attempts: number; errorCode: string | null; updatedAt: string } | null
}

export interface LegalPackArchiveSummary {
  id: string
  archiveName: string
  analysisStatus: string
  documentCount: number
  url: string
  createdAt: string
  analysedAt: string | null
  packContentFingerprint: string
  currentForAnalysis: boolean
  uploadSemantics: 'complete_pack_snapshot'
  documents: LegalPackDocumentSummary[]
}

export interface LegalCitation { legalPackId: string; page: number | null; clause: string | null; quote: string }

export interface LegalCheckpoint { checkpointId: LegalCheckpointId; applicability: LegalApplicability; evidenceStatus: LegalEvidenceStatus; actionStatus: LegalActionStatus; detail: string; citations: LegalCitation[] }

export interface LegalFinding {
  findingId: string
  title: string
  detail: string
  impact: string | null
  classification: 'routine' | 'material'
  applicability: LegalApplicability
  severity: 'information' | 'action' | 'warning' | 'blocker'
  evidenceStatus: LegalEvidenceStatus
  actionStatus: LegalActionStatus
  citations: LegalCitation[]
  relatedCheckpointIds: LegalCheckpointId[]
}

export interface LegalCost {
  costId: string
  label: string
  formula: 'fixed' | 'percent' | 'percent_with_minimum' | 'unknown'
  amountPence: number | null
  rateBasisPoints: number | null
  minimumPence: number | null
  percentageBasis: 'purchase_price' | 'specified_amount' | 'unknown' | null
  percentageBasisAmountPence: number | null
  vatTreatment: 'included' | 'excluded' | 'not_applicable' | 'unknown'
  inclusionGroup: string
  status: 'confirmed' | 'estimated' | 'unknown'
  payee: string | null
  trigger: string | null
  relatedFindingIds: string[]
  relatedActionIds: string[]
  citations: LegalCitation[]
}

export interface LegalFollowUp {
  id: string
  title: string
  detail: string
  status: 'open' | 'resolved' | 'not_applicable'
  responsibleParty: string | null
  dueBy: string | null
  basis: 'proposed' | 'quoted' | 'bound' | null
  relatedFindingIds: string[]
  citations: LegalCitation[]
}

export interface LegalAnalysisDocument {
  legalPackId: string
  contentFingerprint: string
  documentType: string
  readMethod: 'text' | 'render' | 'mixed' | 'not_pdf' | 'unreadable'
  coverage: 'complete' | 'partial' | 'unreadable'
  pages: number | null
  textChars: number | null
  detail: string | null
  documentTypeCorrection: string | null
}

export interface LegalPropertyContext {
  address?: string
  uprn?: string
  titleNumbers: string[]
  tenure?: string
  occupancy?: string
  useClass?: string
  citations: LegalCitation[]
}

export interface LegalSellerContext { names: string[]; registeredProprietor?: boolean; capacity?: string; companyNumbers: string[]; citations: LegalCitation[] }
export interface LegalHistoryEvent { eventType: string; eventDate?: string; detail: string; citations: LegalCitation[] }

export interface LegalAnalysisV3 {
  schemaVersion: 3
  archiveId: string
  packContentFingerprint: string
  propertyContext: LegalPropertyContext
  sellerContext: LegalSellerContext
  history: LegalHistoryEvent[]
  checkpoints: LegalCheckpoint[]
  documents: LegalAnalysisDocument[]
  findings: LegalFinding[]
  costs: LegalCost[]
  actions: LegalFollowUp[]
  mitigations: LegalFollowUp[]
  summary: { headline: string; legalReadiness: 'reviewed' | 'action_required' | 'blocked' | 'incomplete'; materialFindingIds: string[]; openActionIds: string[]; unknowns: string[] }
  analyst: string
  model: string | null
  confidence: 'low' | 'medium' | 'high' | null
}

export interface LegalProcessingFailure {
  code: 'document_preparation_failed' | 'document_reading_failed' | 'document_rendering_unavailable' | 'citation_validation_failed' | 'output_validation_failed' | 'quota_exhausted' | 'processing_timeout' | 'processing_failed'
  message: string
}

export interface CurrentLegalAnalysis {
  propertyId: string
  archiveId: string | null
  currentPackContentFingerprint: string | null
  analysisPackContentFingerprint: string | null
  state: 'missing' | 'ready' | 'pending' | 'current' | 'stale' | 'failed'
  processingReason: 'hard_review' | 'not_applicable' | 'failed_terminal' | null
  processingFailure: LegalProcessingFailure | null
  analysis: LegalAnalysisV3 | null
  reportId: string | null
  updatedAt: string | null
}

export interface LegalPackUploadResult {
  archiveId: string
  archiveName: string
  packContentFingerprint: string
  acceptedDocumentCount: number
  duplicate: boolean
  restoredSnapshot: boolean
  uploadSemantics: 'complete_pack_snapshot'
  documentRemovalCount: number
  skipped: Array<{ entryPath: string; reason: 'directory' | 'empty' | 'resource_fork' | 'nested_archive' | 'symlink' | 'duplicate_entry_path' }>
  warning: string | null
}

export interface LegalPackWorkspace { archives: LegalPackArchiveSummary[]; current: CurrentLegalAnalysis }

/* ---------- parsers ---------- */

type JsonRecord = Record<string, unknown>
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
const SHA256_PATTERN = /^[0-9a-f]{64}$/

function mismatch(message: string): never { throw new ApiError(502, 'legal_pack_contract_mismatch', message) }
function record(value: unknown, name: string): JsonRecord { if (!value || typeof value !== 'object' || Array.isArray(value)) mismatch(`${name} must be an object`); return value as JsonRecord }
function stringValue(value: unknown, name: string): string { if (typeof value !== 'string' || !value.trim()) mismatch(`${name} must be a non-empty string`); return value }
function booleanValue(value: unknown, name: string): boolean { if (typeof value !== 'boolean') mismatch(`${name} must be boolean`); return value }
function nullableString(value: unknown, name: string): string | null { return value === null || value === undefined ? null : stringValue(value, name) }
function optionalBoolean(value: unknown, name: string): boolean | undefined { if (value === undefined || value === null) return undefined; if (typeof value !== 'boolean') mismatch(`${name} must be boolean`); return value }
function uuid(value: unknown, name: string): string { const parsed = stringValue(value, name); if (!UUID_PATTERN.test(parsed)) mismatch(`${name} must be a lowercase UUID`); return parsed }
function nullableUuid(value: unknown, name: string): string | null { return value === null ? null : uuid(value, name) }
function sha256(value: unknown, name: string): string { const parsed = stringValue(value, name); if (!SHA256_PATTERN.test(parsed)) mismatch(`${name} must be a SHA-256 fingerprint`); return parsed }
function nullableSha256(value: unknown, name: string): string | null { return value === null ? null : sha256(value, name) }
function literal<T extends readonly string[]>(value: unknown, values: T, name: string): T[number] { if (typeof value !== 'string' || !values.includes(value)) mismatch(`${name} has an unsupported value`); return value as T[number] }
function array(value: unknown, name: string): unknown[] { if (!Array.isArray(value)) mismatch(`${name} must be an array`); return value }
function stringArray(value: unknown, name: string): string[] { return array(value, name).map((entry, index) => stringValue(entry, `${name}[${index}]`)) }
function integer(value: unknown, name: string): number { if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) mismatch(`${name} must be a non-negative integer`); return value }
function nullableInteger(value: unknown, name: string): number | null { return value === null || value === undefined ? null : integer(value, name) }
function iso(value: unknown, name: string): string { const parsed = stringValue(value, name); if (Number.isNaN(Date.parse(parsed))) mismatch(`${name} must be an ISO timestamp`); return parsed }
function nullableIso(value: unknown, name: string): string | null { return value === null ? null : iso(value, name) }
function isoDate(value: unknown, name: string): string { const parsed = stringValue(value, name); if (!/^\d{4}-\d{2}-\d{2}$/.test(parsed) || Number.isNaN(Date.parse(`${parsed}T00:00:00Z`))) mismatch(`${name} must be an ISO date`); return parsed }
function nullableBoolean(value: unknown, name: string): boolean | null { return value === null || value === undefined ? null : booleanValue(value, name) }
function optionalString(item: JsonRecord, key: string, name: string): string | undefined { return item[key] === undefined || item[key] === null ? undefined : stringValue(item[key], `${name}.${key}`) }

function safeDisplayPath(value: unknown, name: string): string {
  const parsed = stringValue(value, name)
  if (parsed.length > 1_024 || /[\u0000-\u001f\u007f]/.test(parsed)) mismatch(`${name} is unsafe to display`)
  return parsed
}

function assetUrl(value: unknown, name: string): string {
  const safe = safeSameOriginPath(stringValue(value, name))
  if (!safe) mismatch(`${name} must be a same-origin relative URL`)
  return safe
}

function documentUrl(value: unknown, name: string): string {
  const safe = safeDocumentUrl(stringValue(value, name))
  if (!safe) mismatch(`${name} must be a same-origin path or HTTP(S) URL`)
  return safe
}

function nullableAssetUrl(value: unknown, name: string): string | null { return value === null || value === undefined ? null : assetUrl(value, name) }

function parsePreparedAssets(value: unknown, name: string): LegalPackPreparedAssets | null {
  if (value === null || value === undefined) return null
  const item = record(value, name)
  return {
    cacheKey: stringValue(item.cacheKey, `${name}.cacheKey`),
    markdownUrl: assetUrl(item.markdownUrl, `${name}.markdownUrl`),
    markdownSource: literal(item.markdownSource, ['deterministic', 'vision'] as const, `${name}.markdownSource`),
    deterministicMarkdownUrl: assetUrl(item.deterministicMarkdownUrl, `${name}.deterministicMarkdownUrl`),
    textUrl: nullableAssetUrl(item.textUrl, `${name}.textUrl`),
    pageIndexUrl: nullableAssetUrl(item.pageIndexUrl, `${name}.pageIndexUrl`),
    pageCount: nullableInteger(item.pageCount, `${name}.pageCount`),
    markdownChars: nullableInteger(item.markdownChars, `${name}.markdownChars`),
    textChars: nullableInteger(item.textChars, `${name}.textChars`),
    requiresVisualReview: nullableBoolean(item.requiresVisualReview, `${name}.requiresVisualReview`),
    pages: array(item.pages, `${name}.pages`).map((entry, index) => {
      const pageName = `${name}.pages[${index}]`, page = record(entry, pageName)
      const pageNumber = integer(page.page, `${pageName}.page`)
      if (pageNumber === 0) mismatch(`${pageName}.page must be greater than zero`)
      return {
        page: pageNumber,
        imageUrl: assetUrl(page.imageUrl, `${pageName}.imageUrl`),
        mimeType: nullableString(page.mimeType, `${pageName}.mimeType`),
        width: nullableInteger(page.width, `${pageName}.width`),
        height: nullableInteger(page.height, `${pageName}.height`),
        textChars: nullableInteger(page.textChars, `${pageName}.textChars`),
        visionRequired: nullableBoolean(page.visionRequired, `${pageName}.visionRequired`),
      }
    }),
  }
}

function parseProcessingFailure(value: unknown): LegalProcessingFailure | null {
  if (value === null || value === undefined) return null
  const item = record(value, 'current.processingFailure')
  const message = stringValue(item.message, 'current.processingFailure.message')
  const unsafeDiagnostic = /[a-z]:\\|\\\\|https?:\/\/|\/(?:home|run|users?)\/|\b(?:bearer|authorization|api[_ -]?key|password|secret|token)\b/i
  if (message.length > 1_000 || /[\u0000-\u001f\u007f]/.test(message) || unsafeDiagnostic.test(message)) mismatch('current.processingFailure.message is unsafe to display')
  return {
    code: literal(item.code, ['document_preparation_failed', 'document_reading_failed', 'document_rendering_unavailable', 'citation_validation_failed', 'output_validation_failed', 'quota_exhausted', 'processing_timeout', 'processing_failed'] as const, 'current.processingFailure.code'),
    message,
  }
}

function parseDocumentProcessing(value: unknown, name: string): LegalPackDocumentSummary['processing'] {
  if (value === null || value === undefined) return null
  const item = record(value, name)
  const errorCode = nullableString(item.errorCode, `${name}.errorCode`)
  if (errorCode !== null && !/^[a-z0-9_]{1,100}$/i.test(errorCode)) mismatch(`${name}.errorCode must be a safe diagnostic code`)
  return { status: literal(item.status, ['queued', 'running', 'completed', 'failed'] as const, `${name}.status`), attempts: integer(item.attempts, `${name}.attempts`), errorCode, updatedAt: iso(item.updatedAt, `${name}.updatedAt`) }
}

function parseCitation(value: unknown, name: string): LegalCitation {
  const item = record(value, name)
  const page = nullableInteger(item.page, `${name}.page`)
  if (page === 0) mismatch(`${name}.page must be greater than zero`)
  return { legalPackId: uuid(item.legalPackId, `${name}.legalPackId`), page, clause: nullableString(item.clause, `${name}.clause`), quote: stringValue(item.quote, `${name}.quote`) }
}

function citations(value: unknown, name: string): LegalCitation[] { return array(value, name).map((entry, index) => parseCitation(entry, `${name}[${index}]`)) }

const APPLICABILITY = ['applies', 'does_not_apply', 'uncertain'] as const
const EVIDENCE = ['supported', 'missing', 'conflicting', 'not_checked'] as const
const ACTION = ['none', 'routine_action', 'clarification', 'specialist_resolution', 'blocker', 'resolved'] as const

function parseCheckpoint(value: unknown, index: number): LegalCheckpoint {
  const name = `analysis.checkpoints[${index}]`, item = record(value, name)
  const parsed: LegalCheckpoint = {
    checkpointId: literal(item.checkpointId, LEGAL_CHECKPOINT_IDS, `${name}.checkpointId`),
    applicability: literal(item.applicability, APPLICABILITY, `${name}.applicability`),
    evidenceStatus: literal(item.evidenceStatus, EVIDENCE, `${name}.evidenceStatus`),
    actionStatus: literal(item.actionStatus, ACTION, `${name}.actionStatus`),
    detail: stringValue(item.detail, `${name}.detail`), citations: citations(item.citations, `${name}.citations`),
  }
  if (['supported', 'conflicting'].includes(parsed.evidenceStatus) && parsed.citations.length === 0) mismatch(`${name} requires a citation for its evidence status`)
  return parsed
}

function parseFinding(value: unknown, index: number): LegalFinding {
  const name = `analysis.findings[${index}]`, item = record(value, name)
  const classification = literal(item.classification, ['routine', 'material'] as const, `${name}.classification`)
  const severity = literal(item.severity, ['information', 'action', 'warning', 'blocker'] as const, `${name}.severity`)
  const impact = nullableString(item.impact, `${name}.impact`)
  if (classification === 'material' && !impact) mismatch(`${name}.impact is required for material findings`)
  if (classification === 'routine' && (impact || ['warning', 'blocker'].includes(severity))) mismatch(`${name} promotes a routine finding as material`)
  const parsed: LegalFinding = {
    findingId: stringValue(item.findingId, `${name}.findingId`), title: stringValue(item.title, `${name}.title`), detail: stringValue(item.detail, `${name}.detail`), impact, classification, severity,
    applicability: literal(item.applicability, APPLICABILITY, `${name}.applicability`),
    evidenceStatus: literal(item.evidenceStatus, EVIDENCE, `${name}.evidenceStatus`),
    actionStatus: literal(item.actionStatus, ACTION, `${name}.actionStatus`),
    citations: citations(item.citations, `${name}.citations`),
    relatedCheckpointIds: array(item.relatedCheckpointIds, `${name}.relatedCheckpointIds`).map((id, idIndex) => literal(id, LEGAL_CHECKPOINT_IDS, `${name}.relatedCheckpointIds[${idIndex}]`)),
  }
  if (classification === 'routine' && ['specialist_resolution', 'blocker'].includes(parsed.actionStatus)) mismatch(`${name} promotes a routine action as material`)
  if (classification === 'material' && parsed.applicability !== 'does_not_apply' && ['none', 'routine_action'].includes(parsed.actionStatus)) mismatch(`${name} has material impact without an actionable or resolved status`)
  if (['supported', 'conflicting'].includes(parsed.evidenceStatus) && parsed.citations.length === 0) mismatch(`${name} requires a citation for its evidence status`)
  if (['missing', 'not_checked'].includes(parsed.evidenceStatus) && parsed.applicability !== 'uncertain') mismatch(`${name} cannot claim applicability from missing or unchecked evidence`)
  return parsed
}

function parseFollowUp(value: unknown, index: number, kind: 'action' | 'mitigation'): LegalFollowUp {
  const name = `analysis.${kind}s[${index}]`, item = record(value, name)
  return {
    id: stringValue(item[`${kind}Id`], `${name}.${kind}Id`), title: stringValue(item.title, `${name}.title`), detail: stringValue(item.detail, `${name}.detail`),
    status: literal(item.status, ['open', 'resolved', 'not_applicable'] as const, `${name}.status`), responsibleParty: nullableString(item.responsibleParty, `${name}.responsibleParty`),
    dueBy: item.dueBy === null || item.dueBy === undefined ? null : isoDate(item.dueBy, `${name}.dueBy`), basis: kind === 'mitigation' ? literal(item.basis, ['proposed', 'quoted', 'bound'] as const, `${name}.basis`) : null,
    relatedFindingIds: stringArray(item.relatedFindingIds, `${name}.relatedFindingIds`), citations: citations(item.citations, `${name}.citations`),
  }
}

function parseCost(value: unknown, index: number): LegalCost {
  const name = `analysis.costs[${index}]`, item = record(value, name)
  const parsed: LegalCost = {
    costId: stringValue(item.costId, `${name}.costId`), label: stringValue(item.label, `${name}.label`), formula: literal(item.formula, ['fixed', 'percent', 'percent_with_minimum', 'unknown'] as const, `${name}.formula`),
    amountPence: nullableInteger(item.amountPence, `${name}.amountPence`), rateBasisPoints: nullableInteger(item.rateBasisPoints, `${name}.rateBasisPoints`), minimumPence: nullableInteger(item.minimumPence, `${name}.minimumPence`),
    percentageBasis: item.percentageBasis === undefined || item.percentageBasis === null ? null : literal(item.percentageBasis, ['purchase_price', 'specified_amount', 'unknown'] as const, `${name}.percentageBasis`), percentageBasisAmountPence: nullableInteger(item.percentageBasisAmountPence, `${name}.percentageBasisAmountPence`),
    vatTreatment: literal(item.vatTreatment, ['included', 'excluded', 'not_applicable', 'unknown'] as const, `${name}.vatTreatment`), inclusionGroup: stringValue(item.inclusionGroup, `${name}.inclusionGroup`),
    status: literal(item.status, ['confirmed', 'estimated', 'unknown'] as const, `${name}.status`), payee: nullableString(item.payee, `${name}.payee`), trigger: nullableString(item.trigger, `${name}.trigger`),
    relatedFindingIds: stringArray(item.relatedFindingIds, `${name}.relatedFindingIds`), relatedActionIds: stringArray(item.relatedActionIds, `${name}.relatedActionIds`), citations: citations(item.citations, `${name}.citations`),
  }
  if (parsed.formula === 'fixed' && (parsed.amountPence === null || parsed.rateBasisPoints !== null || parsed.minimumPence !== null || parsed.percentageBasis !== null || parsed.percentageBasisAmountPence !== null)) mismatch(`${name} has invalid fixed-cost fields`)
  if (parsed.formula === 'percent' && (parsed.rateBasisPoints === null || parsed.minimumPence !== null || parsed.percentageBasis === null)) mismatch(`${name} has invalid percentage fields`)
  if (parsed.formula === 'percent_with_minimum' && (parsed.rateBasisPoints === null || parsed.minimumPence === null || parsed.percentageBasis === null)) mismatch(`${name} has invalid percentage-with-minimum fields`)
  if (parsed.formula === 'unknown' && [parsed.amountPence, parsed.rateBasisPoints, parsed.minimumPence, parsed.percentageBasis, parsed.percentageBasisAmountPence].some((field) => field !== null)) mismatch(`${name} unknown formula cannot carry numeric or basis fields`)
  if (parsed.status === 'unknown' && parsed.formula !== 'unknown') mismatch(`${name} unknown status requires unknown formula`)
  if (parsed.status !== 'unknown' && parsed.citations.length === 0) mismatch(`${name} confirmed or estimated cost requires a citation`)
  if (parsed.percentageBasis === 'specified_amount' && parsed.percentageBasisAmountPence === null) mismatch(`${name}.percentageBasisAmountPence is required for a specified amount`)
  if (parsed.percentageBasis !== 'specified_amount' && parsed.percentageBasisAmountPence !== null) mismatch(`${name}.percentageBasisAmountPence is only valid for a specified amount`)
  return parsed
}

function finite(value: unknown, name: string): number { if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) mismatch(`${name} must be a non-negative number`); return value }

/** Tenure facts are not displayed here, but a malformed set still fails the contract as it did in the workbench. */
function validateTenureFacts(value: unknown, name: string): void {
  if (value === undefined || value === null) return
  const facts = record(value, name)
  const populated = ['leaseDate', 'termStartDate', 'termYears', 'unexpiredYears', 'unexpiredYearsAsAt', 'groundRentPence', 'groundRentReview', 'serviceChargePence', 'serviceChargePeriod'].some((key) => facts[key] !== undefined && facts[key] !== null)
    || (Array.isArray(facts.useRestrictions) && facts.useRestrictions.length > 0)
  const factCitations = citations(facts.citations, `${name}.citations`)
  if (populated && !factCitations.length) mismatch(`${name}.citations is required when a tenure fact is populated`)
  if (facts.unexpiredYears !== undefined && facts.unexpiredYearsAsAt === undefined) mismatch(`${name}.unexpiredYearsAsAt is required with unexpiredYears`)
  for (const key of ['leaseDate', 'termStartDate', 'unexpiredYearsAsAt']) if (facts[key] !== undefined && facts[key] !== null) isoDate(facts[key], `${name}.${key}`)
  for (const key of ['termYears', 'unexpiredYears']) if (facts[key] !== undefined && facts[key] !== null) finite(facts[key], `${name}.${key}`)
  for (const key of ['groundRentPence', 'serviceChargePence']) if (facts[key] !== undefined && facts[key] !== null) integer(facts[key], `${name}.${key}`)
  optionalString(facts, 'groundRentReview', name); optionalString(facts, 'serviceChargePeriod', name)
  stringArray(facts.useRestrictions, `${name}.useRestrictions`)
}

function parsePropertyContext(value: unknown): LegalPropertyContext {
  const name = 'analysis.propertyContext', item = record(value, name)
  validateTenureFacts(item.tenureFacts, `${name}.tenureFacts`)
  return {
    address: optionalString(item, 'address', name), uprn: optionalString(item, 'uprn', name), titleNumbers: stringArray(item.titleNumbers, `${name}.titleNumbers`),
    tenure: optionalString(item, 'tenure', name), occupancy: optionalString(item, 'occupancy', name), useClass: optionalString(item, 'useClass', name), citations: citations(item.citations, `${name}.citations`),
  }
}

function parseSellerContext(value: unknown): LegalSellerContext {
  const name = 'analysis.sellerContext', item = record(value, name)
  return { names: stringArray(item.names, `${name}.names`), registeredProprietor: optionalBoolean(item.registeredProprietor, `${name}.registeredProprietor`), capacity: optionalString(item, 'capacity', name), companyNumbers: stringArray(item.companyNumbers, `${name}.companyNumbers`), citations: citations(item.citations, `${name}.citations`) }
}

function parseHistory(value: unknown): LegalHistoryEvent[] {
  return array(value, 'analysis.history').map((entry, index) => { const name = `analysis.history[${index}]`, item = record(entry, name); return { eventType: stringValue(item.eventType, `${name}.eventType`), eventDate: item.eventDate === undefined || item.eventDate === null ? undefined : isoDate(item.eventDate, `${name}.eventDate`), detail: stringValue(item.detail, `${name}.detail`), citations: citations(item.citations, `${name}.citations`) } })
}

function parseVerification(value: unknown): void {
  const name = 'analysis.verification', item = record(value, name)
  const checks = array(item.checks, `${name}.checks`).map((entry, index) => {
    const checkName = `${name}.checks[${index}]`, check = record(entry, checkName)
    for (const key of ['checkId', 'subject', 'method', 'detail']) stringValue(check[key], `${checkName}.${key}`)
    literal(check.outcome, ['confirmed', 'contradicted', 'inconclusive'] as const, `${checkName}.outcome`)
    return citations(check.citations, `${checkName}.citations`)
  })
  if (checks.length === 0 || checks.some((cited) => cited.length === 0)) mismatch(`${name}.checks must include at least one cited independent check`)
  stringValue(item.verifiedBy, `${name}.verifiedBy`); iso(item.verifiedAt, `${name}.verifiedAt`)
}

function parseAnalysis(value: unknown): LegalAnalysisV3 {
  const item = record(value, 'analysis')
  if (item.schemaVersion !== 3) mismatch('analysis.schemaVersion must be 3')
  const checkpoints = array(item.checkpoints, 'analysis.checkpoints').map(parseCheckpoint)
  if (checkpoints.length !== LEGAL_CHECKPOINT_IDS.length || new Set(checkpoints.map((entry) => entry.checkpointId)).size !== LEGAL_CHECKPOINT_IDS.length) mismatch('analysis.checkpoints must contain the exact 14-checkpoint catalogue once each')
  const documents = array(item.documents, 'analysis.documents').map((entry, index): LegalAnalysisDocument => {
    const name = `analysis.documents[${index}]`, document = record(entry, name)
    return { legalPackId: uuid(document.legalPackId, `${name}.legalPackId`), contentFingerprint: sha256(document.contentFingerprint, `${name}.contentFingerprint`), documentType: stringValue(document.documentType, `${name}.documentType`), readMethod: literal(document.readMethod, ['text', 'render', 'mixed', 'not_pdf', 'unreadable'] as const, `${name}.readMethod`), coverage: literal(document.coverage, ['complete', 'partial', 'unreadable'] as const, `${name}.coverage`), pages: nullableInteger(document.pages, `${name}.pages`), textChars: nullableInteger(document.textChars, `${name}.textChars`), detail: nullableString(document.detail, `${name}.detail`), documentTypeCorrection: nullableString(document.documentTypeCorrection, `${name}.documentTypeCorrection`) }
  })
  const findings = array(item.findings, 'analysis.findings').map(parseFinding)
  const costs = array(item.costs, 'analysis.costs').map(parseCost)
  const actions = array(item.actions, 'analysis.actions').map((entry, index) => parseFollowUp(entry, index, 'action'))
  const mitigations = array(item.mitigations, 'analysis.mitigations').map((entry, index) => parseFollowUp(entry, index, 'mitigation'))
  for (const mitigation of mitigations) {
    if (mitigation.status === 'resolved' && mitigation.basis !== 'bound') mismatch('A resolved mitigation must have bound evidence')
    if (mitigation.basis === 'bound' && (mitigation.status !== 'resolved' || mitigation.citations.length === 0)) mismatch('A bound mitigation must be resolved and cite documentary evidence')
  }
  const summaryValue = record(item.summary, 'analysis.summary')
  const summary = { headline: stringValue(summaryValue.headline, 'analysis.summary.headline'), legalReadiness: literal(summaryValue.legalReadiness, ['reviewed', 'action_required', 'blocked', 'incomplete'] as const, 'analysis.summary.legalReadiness'), materialFindingIds: stringArray(summaryValue.materialFindingIds, 'analysis.summary.materialFindingIds'), openActionIds: stringArray(summaryValue.openActionIds, 'analysis.summary.openActionIds'), unknowns: stringArray(summaryValue.unknowns, 'analysis.summary.unknowns') }
  const findingIds = new Set(findings.map((finding) => finding.findingId))
  if (summary.materialFindingIds.some((id) => !findingIds.has(id))) mismatch('analysis.summary.materialFindingIds references an unknown finding')
  const actionIds = new Set(actions.map((action) => action.id))
  if (summary.openActionIds.some((id) => !actionIds.has(id))) mismatch('analysis.summary.openActionIds references an unknown action')
  if (costs.some((cost) => cost.relatedFindingIds.some((id) => !findingIds.has(id)) || cost.relatedActionIds.some((id) => !actionIds.has(id)))) mismatch('analysis.costs references an unknown finding or action')
  parseVerification(item.verification)
  return {
    schemaVersion: 3, archiveId: uuid(item.archiveId, 'analysis.archiveId'), packContentFingerprint: sha256(item.packContentFingerprint, 'analysis.packContentFingerprint'),
    propertyContext: parsePropertyContext(item.propertyContext), sellerContext: parseSellerContext(item.sellerContext), history: parseHistory(item.history), checkpoints, documents, findings,
    costs, actions, mitigations, summary,
    analyst: stringValue(item.analyst, 'analysis.analyst'), model: nullableString(item.model, 'analysis.model'), confidence: item.confidence === null || item.confidence === undefined ? null : literal(item.confidence, ['low', 'medium', 'high'] as const, 'analysis.confidence'),
  }
}

export function parseCurrentLegalAnalysisEnvelope(value: unknown, expectedPropertyId: string): CurrentLegalAnalysis {
  const envelope = record(value, 'current legal analysis envelope'), item = record(envelope.data, 'current legal analysis envelope.data')
  const parsed: CurrentLegalAnalysis = {
    propertyId: uuid(item.propertyId, 'current.propertyId'), archiveId: nullableUuid(item.archiveId, 'current.archiveId'), currentPackContentFingerprint: nullableSha256(item.currentPackContentFingerprint, 'current.currentPackContentFingerprint'),
    analysisPackContentFingerprint: nullableSha256(item.analysisPackContentFingerprint, 'current.analysisPackContentFingerprint'), state: literal(item.state, ['missing', 'ready', 'pending', 'current', 'stale', 'failed'] as const, 'current.state'),
    processingReason: item.processingReason === null || item.processingReason === undefined ? null : literal(item.processingReason, ['hard_review', 'not_applicable', 'failed_terminal'] as const, 'current.processingReason'), processingFailure: parseProcessingFailure(item.processingFailure),
    analysis: item.analysis === null ? null : parseAnalysis(item.analysis), reportId: nullableUuid(item.reportId, 'current.reportId'), updatedAt: nullableIso(item.updatedAt, 'current.updatedAt'),
  }
  if (parsed.propertyId !== expectedPropertyId) mismatch('Current legal analysis belongs to another property')
  if (parsed.state === 'current' && (!parsed.analysis || parsed.currentPackContentFingerprint !== parsed.analysisPackContentFingerprint || parsed.analysis.packContentFingerprint !== parsed.analysisPackContentFingerprint)) mismatch('Current legal analysis does not match the current pack content')
  if (parsed.state === 'current' && (!parsed.reportId || !parsed.updatedAt)) mismatch('Current legal analysis is missing its published report identity')
  if (parsed.state !== 'current' && parsed.analysis !== null) mismatch('Only a fingerprint-current analysis may expose conclusions')
  if (parsed.state !== 'current' && parsed.reportId !== null) mismatch('Only a fingerprint-current analysis may expose a report identity')
  if (parsed.state !== 'failed' && parsed.processingReason !== null) mismatch('Only failed current-analysis state may carry a processing reason')
  if (parsed.state !== 'failed' && parsed.processingFailure !== null) mismatch('Only failed current-analysis state may carry a processing failure')
  if (parsed.state === 'failed' && parsed.processingFailure === null) mismatch('Failed current-analysis state must explain its safe failure category')
  return parsed
}

export function parseLegalPackListEnvelope(value: unknown, expectedPropertyId: string): LegalPackArchiveSummary[] {
  const envelope = record(value, 'legal pack list envelope'), item = record(envelope.data, 'legal pack list envelope.data')
  if (uuid(item.propertyId, 'legal pack list propertyId') !== expectedPropertyId) mismatch('Legal-pack archive list belongs to another property')
  return array(item.archives, 'legal pack archives').map((entry, index): LegalPackArchiveSummary => {
    const name = `legal pack archives[${index}]`, archive = record(entry, name)
    if (typeof archive.currentForAnalysis !== 'boolean') mismatch(`${name}.currentForAnalysis must be boolean`)
    return {
      id: uuid(archive.id, `${name}.id`), archiveName: stringValue(archive.archiveName, `${name}.archiveName`), analysisStatus: stringValue(archive.analysisStatus, `${name}.analysisStatus`),
      documentCount: integer(archive.documentCount, `${name}.documentCount`), url: documentUrl(archive.url, `${name}.url`), createdAt: iso(archive.createdAt, `${name}.createdAt`), analysedAt: nullableIso(archive.analysedAt, `${name}.analysedAt`),
      packContentFingerprint: sha256(archive.packContentFingerprint, `${name}.packContentFingerprint`), currentForAnalysis: archive.currentForAnalysis, uploadSemantics: literal(archive.uploadSemantics, ['complete_pack_snapshot'] as const, `${name}.uploadSemantics`),
      documents: array(archive.documents, `${name}.documents`).map((doc, documentIndex) => { const documentName = `${name}.documents[${documentIndex}]`, document = record(doc, documentName); return { id: uuid(document.id, `${documentName}.id`), archiveId: uuid(document.archiveId, `${documentName}.archiveId`), documentName: stringValue(document.documentName, `${documentName}.documentName`), url: safeDocumentUrl(nullableString(document.url, `${documentName}.url`)), preparedAssets: parsePreparedAssets(document.preparedAssets, `${documentName}.preparedAssets`), processing: parseDocumentProcessing(document.processing, `${documentName}.processing`) } }),
    }
  })
}

export function parseLegalPackUploadEnvelope(value: unknown, expectedPropertyId: string): LegalPackUploadResult {
  const envelope = record(value, 'legal pack upload envelope')
  const data = record(envelope.data, 'legal pack upload envelope.data')
  const archive = record(data.archive, 'legal pack upload archive')
  if (uuid(archive.propertyId, 'legal pack upload archive.propertyId') !== expectedPropertyId) mismatch('Uploaded legal pack belongs to another property')
  if (!booleanValue(archive.currentForAnalysis, 'legal pack upload archive.currentForAnalysis')) mismatch('Uploaded legal pack was not selected as the current snapshot')
  literal(archive.uploadSemantics, ['complete_pack_snapshot'] as const, 'legal pack upload archive.uploadSemantics')
  const documents = array(data.documents, 'legal pack upload documents')
  documents.forEach((entry, index) => {
    const document = record(entry, `legal pack upload documents[${index}]`)
    uuid(document.id, `legal pack upload documents[${index}].id`)
    safeDisplayPath(document.documentName, `legal pack upload documents[${index}].documentName`)
  })
  if (integer(archive.documentCount, 'legal pack upload archive.documentCount') !== documents.length) mismatch('Uploaded archive document count is inconsistent')
  const duplicate = booleanValue(data.duplicate, 'legal pack upload duplicate')
  const restoredSnapshot = data.restoredSnapshot === undefined ? false : booleanValue(data.restoredSnapshot, 'legal pack upload restoredSnapshot')
  if (duplicate !== restoredSnapshot) mismatch('Upload reuse and restored-snapshot flags are inconsistent')
  return {
    archiveId: uuid(archive.id, 'legal pack upload archive.id'),
    archiveName: safeDisplayPath(archive.archiveName, 'legal pack upload archive.archiveName'),
    packContentFingerprint: sha256(archive.packContentFingerprint, 'legal pack upload archive.packContentFingerprint'),
    acceptedDocumentCount: documents.length,
    duplicate,
    restoredSnapshot,
    uploadSemantics: literal(data.uploadSemantics, ['complete_pack_snapshot'] as const, 'legal pack upload uploadSemantics'),
    documentRemovalCount: integer(data.documentRemovalCount, 'legal pack upload documentRemovalCount'),
    skipped: array(data.skipped, 'legal pack upload skipped').map((entry, index) => {
      const item = record(entry, `legal pack upload skipped[${index}]`)
      return { entryPath: safeDisplayPath(item.entryPath, `legal pack upload skipped[${index}].entryPath`), reason: literal(item.reason, ['directory', 'empty', 'resource_fork', 'nested_archive', 'symlink', 'duplicate_entry_path'] as const, `legal pack upload skipped[${index}].reason`) }
    }),
    warning: nullableString(data.warning, 'legal pack upload warning'),
  }
}

/* ---------- hooks ---------- */

export const legalKeys = { workspace: (propertyId: string) => ['legalPack', propertyId] as const }

/** A pending analysis is re-read every 5 s, a failed one every 15 s (a retried job can replace it); otherwise no timer. */
export function legalWorkspaceRefetchInterval(current: CurrentLegalAnalysis | undefined): number | false {
  if (current?.state === 'pending') return 5_000
  if (current?.state === 'failed') return 15_000
  return false
}

export function useLegalPackWorkspace(propertyId: string) {
  return useQuery({
    queryKey: legalKeys.workspace(propertyId),
    queryFn: async ({ signal }): Promise<LegalPackWorkspace> => {
      const [packs, current] = await Promise.all([
        api<unknown>('legalPacks', { params: { propertyId }, signal }),
        api<unknown>('legalAnalysisCurrent', { params: { propertyId }, signal }),
      ])
      return { archives: parseLegalPackListEnvelope(packs, propertyId), current: parseCurrentLegalAnalysisEnvelope(current, propertyId) }
    },
    enabled: propertyId.length > 0,
    retry: false,
    refetchInterval: (query) => legalWorkspaceRefetchInterval(query.state.data?.current),
  })
}

export function useUploadLegalPack(propertyId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file, file.name)
      form.append('uploadedBy', 'ui')
      return parseLegalPackUploadEnvelope(await api<unknown>('legalPackUpload', { params: { propertyId }, form }), propertyId)
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.property(propertyId) }),
        client.invalidateQueries({ queryKey: legalKeys.workspace(propertyId) }),
        client.invalidateQueries({ queryKey: ['calculationCards'] }),
      ])
    },
  })
}

export function useRequestLegalAnalysis(propertyId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async () => api<unknown>('legalAnalysisRequest', { params: { propertyId } }),
    onSuccess: async () => { await client.invalidateQueries({ queryKey: legalKeys.workspace(propertyId) }) },
  })
}
